import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Search, Loader2, BookOpen, Users, BarChart2, Download, Printer } from "lucide-react";
import { getApiUrl } from "../../config/api";
import { TERM_OPTIONS, ASSESSMENT_TYPES } from "../../constants/assessments";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";

interface ClassItem {
  id: string;
  name: string;
}

interface SubjectItem {
  id: string;
  name: string;
}

interface StudentItem {
  id: string;
  name: string;
  classId: string;
  studentId?: string;
  admissionNumber?: string;
}

interface ScoreEntry {
  studentId: string;
  subjectId: string;
  score: number | "";
  resultId?: string;
}

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 11 }, (_, i) => (currentYear - 5 + i).toString());

const scoreColor = (score: number | "") => {
  if (score === "" || score === undefined) return "text-slate-400";
  if (score >= 80) return "text-emerald-600 font-bold";
  if (score >= 50) return "text-blue-600 font-semibold";
  return "text-red-500 font-semibold";
};

const scoreBg = (score: number | "") => {
  if (score === "" || score === undefined) return "bg-slate-50";
  if (score >= 80) return "bg-emerald-50";
  if (score >= 50) return "bg-blue-50";
  return "bg-red-50";
};

const abbreviateSubject = (name: string) => {
  const cleaned = name.trim().toUpperCase();
  if (cleaned === "CREATIVE AND TECHNOLOGY STUDIES") return "CTS";
  if (cleaned === "HOME ECONOMICS") return "HE";
  if (cleaned === "PHYSICAL EDUCATION") return "P.E.";
  if (cleaned === "SCIENCE PAPER 1") return "SP1";
  if (cleaned === "SCIENCE PAPER 2") return "SP2";
  return name;
};

const HeadteacherResults: React.FC = () => {
  const token = localStorage.getItem("token");
  const [classes, setClasses] = useState<ClassItem[]>([]);
  const [subjects, setSubjects] = useState<SubjectItem[]>([]);
  const [students, setStudents] = useState<StudentItem[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<StudentItem[]>([]);
  const [scores, setScores] = useState<ScoreEntry[]>([]);

  const [selectedClass, setSelectedClass] = useState("");
  const [selectedYear, setSelectedYear] = useState(currentYear.toString());
  const [selectedTerm, setSelectedTerm] = useState("");
  const [selectedAssessmentType, setSelectedAssessmentType] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [loading, setLoading] = useState(false);
  const [loadingResults, setLoadingResults] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const filtersReady = !!selectedClass && !!selectedTerm && !!selectedAssessmentType && !!selectedYear;

  const fetchClasses = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(getApiUrl("academics/classes"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const classesData = Array.isArray(res.data) ? res.data : res.data.classes || [];
      setClasses(classesData.map((c: any) => ({ id: String(c.id || c._id), name: c.name })));
    } catch (err) {
      console.error("Error fetching classes:", err);
      setError("Failed to load classes.");
    }
  }, [token]);

  const fetchSubjects = useCallback(async () => {
    if (!selectedClass || !token) {
      setSubjects([]);
      return;
    }
    try {
      const res = await axios.get(getApiUrl(`academics/classes/${selectedClass}/subjects`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = Array.isArray(res.data) ? res.data : res.data.subjects || [];
      setSubjects(data.map((s: any) => ({ id: String(s.id || s._id), name: s.name })));
    } catch (err) {
      console.error("Error fetching subjects:", err);
      setSubjects([]);
      setError("Failed to load class subjects.");
    }
  }, [selectedClass, token]);

  const fetchStudents = useCallback(async () => {
    if (!selectedClass || !token) {
      setStudents([]);
      setFilteredStudents([]);
      setScores([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(getApiUrl(`academics/classes/${selectedClass}/students`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = Array.isArray(res.data) ? res.data : res.data.students || [];
      const mapped: StudentItem[] = data.map((s: any) => ({
        id: String(s.id || s._id),
        name: s.name || s.fullName || "Unknown",
        classId: selectedClass,
        studentId: s.studentId,
        admissionNumber: s.admissionNumber
      }));

      setStudents(mapped);
      setFilteredStudents(mapped);

      if (subjects.length > 0) {
        const initialScores: ScoreEntry[] = [];
        mapped.forEach((stu) => {
          subjects.forEach((sub) => {
            initialScores.push({ studentId: stu.id, subjectId: sub.id, score: "" });
          });
        });
        setScores(initialScores);
      }
    } catch (err: any) {
      console.error("Error fetching students:", err);
      setError(err.response?.data?.message || "Failed to load learners.");
      setStudents([]);
      setFilteredStudents([]);
      setScores([]);
    } finally {
      setLoading(false);
    }
  }, [selectedClass, subjects, token]);

  const fetchExistingResults = useCallback(async () => {
    if (!filtersReady || !token) return;
    setLoadingResults(true);
    setError(null);

    try {
      const url = `assessments/results/class/${selectedClass}?examType=${encodeURIComponent(selectedAssessmentType)}&term=${encodeURIComponent(selectedTerm)}&year=${encodeURIComponent(selectedYear)}`;
      const res = await axios.get(getApiUrl(url), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const results = Array.isArray(res.data) ? res.data : [];

      const map = new Map<string, { id: string; score: number }>();
      results.forEach((r: any) => {
        const sid = String(r.studentId || "");
        const subId = String(r.subjectId || "");
        if (sid && subId) {
          map.set(`${sid}-${subId}`, { id: String(r.id), score: Number(r.score) });
        }
      });

      setScores((prev) => {
        if (prev.length === 0) return prev;
        return prev.map((entry) => {
          const match = map.get(`${entry.studentId}-${entry.subjectId}`);
          return match
            ? { ...entry, score: match.score, resultId: match.id }
            : { ...entry, score: "", resultId: undefined };
        });
      });
    } catch (err: any) {
      console.error("Error fetching results:", err);
      setError(err.response?.data?.message || "Failed to load results.");
    } finally {
      setLoadingResults(false);
    }
  }, [filtersReady, selectedAssessmentType, selectedClass, selectedTerm, selectedYear, token]);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  useEffect(() => {
    if (selectedClass) fetchSubjects();
    else setSubjects([]);
  }, [selectedClass, fetchSubjects]);

  useEffect(() => {
    if (selectedClass && subjects.length > 0) fetchStudents();
  }, [selectedClass, subjects.length, fetchStudents]);

  useEffect(() => {
    if (filtersReady && students.length > 0 && scores.length > 0) {
      fetchExistingResults();
    }
  }, [fetchExistingResults, filtersReady, scores.length, students.length]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredStudents(students);
      return;
    }

    setFilteredStudents(
      students.filter((student) => student.name.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [searchTerm, students]);

  useEffect(() => {
    setSearchTerm("");
    setScores([]);
    setMessage(null);
    setError(null);
  }, [selectedClass]);

  const getScore = useCallback((studentId: string, subjectId: string): number | "" => {
    const entry = scores.find((score) => score.studentId === studentId && score.subjectId === subjectId);
    return entry?.score ?? "";
  }, [scores]);

  const getAverage = useCallback((studentId: string): number | null => {
    const studentScores = scores
      .filter((entry) => entry.studentId === studentId && entry.score !== "")
      .map((entry) => Number(entry.score));

    if (studentScores.length === 0) return null;
    return studentScores.reduce((sum, score) => sum + score, 0) / studentScores.length;
  }, [scores]);

  const getTotal = useCallback((studentId: string): number | null => {
    const studentScores = scores
      .filter((entry) => entry.studentId === studentId && entry.score !== "")
      .map((entry) => Number(entry.score));

    if (studentScores.length === 0) return null;
    return studentScores.reduce((sum, score) => sum + score, 0);
  }, [scores]);

  const scoreSummary = useMemo(() => {
    const numericScores = scores
      .map((entry) => entry.score)
      .filter((score): score is number => typeof score === "number" && !Number.isNaN(score));

    const passingLearners = filteredStudents.filter((student) => {
      const avg = getAverage(student.id);
      return avg !== null && avg >= 50;
    }).length;

    return {
      learners: filteredStudents.length,
      subjects: subjects.length,
      enteredScores: numericScores.length,
      classAverage: numericScores.length
        ? numericScores.reduce((sum, score) => sum + score, 0) / numericScores.length
        : null,
      passingLearners
    };
  }, [filteredStudents, getAverage, scores, subjects.length]);

  const downloadReport = async (format: "pdf" | "word") => {
    if (!selectedClass || !selectedAssessmentType || !selectedTerm) {
      setError("Select class, term, and assessment type before downloading.");
      return;
    }

    try {
      const response = await axios.get(getApiUrl(`export/${format}/assessments`), {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          classId: selectedClass,
          examType: selectedAssessmentType,
          term: selectedTerm,
          year: selectedYear
        },
        responseType: "blob"
      });

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `results_oversight_${selectedAssessmentType}_${selectedTerm}_${selectedYear}.${format === "pdf" ? "pdf" : "docx"}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to download report.");
    }
  };

  return (
    <div className="utopia-page">
      <div className="utopia-page-header">
        <div>
          <h1 className="utopia-page-title">Results Oversight</h1>
          <p className="page-subtitle">
            Review class results in a bulk score-sheet view. This page is read-only for headteachers.
          </p>
        </div>
        {filtersReady && filteredStudents.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              className="utopia-btn utopia-btn-secondary flex items-center gap-2"
              onClick={() => downloadReport("pdf")}
            >
              <Download size={16} />
              PDF
            </button>
            <button
              className="utopia-btn utopia-btn-secondary flex items-center gap-2"
              onClick={() => downloadReport("word")}
            >
              <Download size={16} />
              Word
            </button>
            <button
              className="utopia-btn utopia-btn-secondary flex items-center gap-2"
              onClick={() => window.print()}
            >
              <Printer size={16} />
              Print
            </button>
          </div>
        )}
      </div>

      {(message || error) && (
        <div className="mb-4 space-y-3">
          {message && (
            <div className="p-3 bg-teal-50 border border-teal-200 rounded-xl text-teal-700 text-sm">
              {message}
            </div>
          )}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              {error}
            </div>
          )}
        </div>
      )}

      <div className="utopia-card mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Class</label>
            <select
              className="utopia-input"
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
            >
              <option value="">Select Class...</option>
              {classes.map((cls) => (
                <option key={cls.id} value={cls.id}>{cls.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Year</label>
            <select
              className="utopia-input"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              {YEARS.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Term</label>
            <select
              className="utopia-input"
              value={selectedTerm}
              onChange={(e) => setSelectedTerm(e.target.value)}
            >
              <option value="">Select Term...</option>
              {TERM_OPTIONS.map((term) => (
                <option key={term} value={term}>{term}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Assessment Type</label>
            <select
              className="utopia-input"
              value={selectedAssessmentType}
              onChange={(e) => setSelectedAssessmentType(e.target.value)}
            >
              <option value="">Select Assessment Type...</option>
              {ASSESSMENT_TYPES.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Search Learner</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
              <input
                type="text"
                placeholder="Search by name..."
                className="utopia-search-input pl-9"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {filtersReady && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          <div className="utopia-card">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Learners</p>
            <p className="mt-3 text-3xl font-black tracking-tight text-slate-900">{scoreSummary.learners}</p>
            <p className="mt-2 text-sm text-slate-500">Visible in the current result sheet</p>
          </div>
          <div className="utopia-card">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Subjects</p>
            <p className="mt-3 text-3xl font-black tracking-tight text-slate-900">{scoreSummary.subjects}</p>
            <p className="mt-2 text-sm text-slate-500">Columns loaded for the selected class</p>
          </div>
          <div className="utopia-card">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Scores Loaded</p>
            <p className="mt-3 text-3xl font-black tracking-tight text-slate-900">{scoreSummary.enteredScores}</p>
            <p className="mt-2 text-sm text-slate-500">Saved marks found for this filter set</p>
          </div>
          <div className="utopia-card">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Class Average</p>
            <p className="mt-3 text-3xl font-black tracking-tight text-slate-900">
              {scoreSummary.classAverage !== null ? `${scoreSummary.classAverage.toFixed(1)}%` : "—"}
            </p>
            <p className="mt-2 text-sm text-slate-500">
              {scoreSummary.passingLearners} learner{scoreSummary.passingLearners === 1 ? "" : "s"} averaging 50% or above
            </p>
          </div>
        </div>
      )}

      {!selectedClass ? (
        <div className="utopia-card flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-indigo-50 flex items-center justify-center mb-4">
            <BookOpen className="text-indigo-400" size={32} />
          </div>
          <h3 className="text-lg font-semibold text-slate-700 mb-1">Select a class to get started</h3>
          <p className="text-slate-500 text-sm">Choose a class, year, term, and assessment type to review the bulk score sheet.</p>
        </div>
      ) : loading ? (
        <div className="utopia-card flex flex-col items-center justify-center py-20">
          <Loader2 className="animate-spin text-indigo-500 mb-3" size={36} />
          <p className="text-slate-500 font-medium">Loading learners...</p>
        </div>
      ) : students.length === 0 ? (
        <div className="utopia-card flex flex-col items-center justify-center py-20 text-center">
          <Users className="text-slate-300 mb-4" size={48} />
          <p className="text-slate-500 font-medium">No learners found in this class.</p>
        </div>
      ) : !selectedAssessmentType || !selectedTerm ? (
        <div className="utopia-card flex flex-col items-center justify-center py-16 text-center">
          <BarChart2 className="text-slate-300 mb-4" size={48} />
          <p className="text-slate-600 font-semibold">
            {students.length} learner{students.length !== 1 ? "s" : ""} loaded
          </p>
          <p className="text-slate-400 text-sm mt-1">Select both term and assessment type to load the score grid.</p>
        </div>
      ) : (
        <div className="utopia-card p-0 overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b border-slate-100">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-slate-700">
                {filteredStudents.length} learner{filteredStudents.length !== 1 ? "s" : ""}
              </span>
              {loadingResults && (
                <span className="flex items-center gap-1.5 text-xs text-indigo-600 bg-indigo-50 px-2 py-1 rounded-full">
                  <Loader2 className="animate-spin" size={12} />
                  Loading scores...
                </span>
              )}
              <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-medium">
                Read Only
              </span>
            </div>
            <p className="text-xs text-slate-500 font-medium">
              Bulk score-sheet view aligned with the teacher results layout
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="sticky left-0 bg-slate-50 text-left py-3 px-4 font-semibold text-slate-600 whitespace-nowrap z-10 border-r border-slate-200">
                    #
                  </th>
                  <th className="sticky left-8 bg-slate-50 text-left py-3 px-4 font-semibold text-slate-600 whitespace-nowrap z-10 min-w-[220px] border-r border-slate-200">
                    Learner
                  </th>
                  {subjects.map((subject) => (
                    <th
                      key={subject.id}
                      className="py-3 px-4 font-semibold text-slate-600 text-center whitespace-nowrap min-w-[90px]"
                      title={subject.name}
                    >
                      {abbreviateSubject(subject.name)}
                    </th>
                  ))}
                  <th className="py-3 px-4 font-semibold text-slate-700 text-center whitespace-nowrap bg-slate-100">
                    Total
                  </th>
                  <th className="py-3 px-4 font-semibold text-slate-700 text-center whitespace-nowrap bg-slate-100">
                    Average
                  </th>
                  <th className="py-3 px-4 font-semibold text-slate-700 text-center whitespace-nowrap bg-slate-100">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredStudents.length === 0 ? (
                  <tr>
                    <td colSpan={subjects.length + 5} className="py-12 text-center text-slate-400">
                      No learners match your search.
                    </td>
                  </tr>
                ) : (
                  filteredStudents.map((student, index) => {
                    const total = getTotal(student.id);
                    const average = getAverage(student.id);
                    const isPassing = average !== null && average >= 50;

                    return (
                      <tr key={student.id} className="hover:bg-slate-50/60 transition-colors">
                        <td className="sticky left-0 bg-white py-3 px-4 text-slate-400 font-medium text-xs border-r border-slate-100 z-10">
                          {index + 1}
                        </td>
                        <td className="sticky left-8 bg-white py-3 px-4 border-r border-slate-100 z-10">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                              {student.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-semibold text-slate-800 leading-tight">{student.name}</p>
                              {(student.admissionNumber || student.studentId) && (
                                <p className="text-xs text-slate-400">{student.admissionNumber || student.studentId}</p>
                              )}
                            </div>
                          </div>
                        </td>

                        {subjects.map((subject) => {
                          const score = getScore(student.id, subject.id);
                          return (
                            <td key={subject.id} className={`py-2 px-3 text-center ${scoreBg(score)}`}>
                              {score === "" ? (
                                <span className="text-slate-300 text-xs">—</span>
                              ) : (
                                <span className={`text-sm ${scoreColor(score)}`}>{score}</span>
                              )}
                            </td>
                          );
                        })}

                        <td className="py-2 px-4 text-center bg-slate-50/80">
                          {total !== null ? (
                            <span className={`text-sm font-bold ${scoreColor(total)}`}>
                              {total.toFixed(1)}
                            </span>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </td>

                        <td className="py-2 px-4 text-center bg-slate-50/80">
                          {average !== null ? (
                            <span className={`text-sm font-bold ${scoreColor(average)}`}>
                              {average.toFixed(1)}%
                            </span>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </td>

                        <td className="py-2 px-4 text-center bg-slate-50/80">
                          {average !== null ? (
                            <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-bold ${
                              isPassing ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
                            }`}>
                              {isPassing ? "PASS" : "FAIL"}
                            </span>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeadteacherResults;
